import { createContext, useContext, useEffect, useState } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { auth, db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const signup = async (email, password, fullName, phone, userType = 'Individual', vendorName = '', slug = '') => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userData = {
            uid: user.uid,
            email,
            fullName,
            phone,
            userType,
            createdAt: new Date()
        };

        // Add vendor-specific fields for Corporate users
        if (userType === 'Corporate') {
            userData.vendorName = vendorName;
            userData.slug = slug;
            userData.logo = '';
            userData.coverPhoto = '';
        }

        // Save extra user details to Firestore
        await setDoc(doc(db, "users", user.uid), userData);

        return user;
    };

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        const user = userCredential.user;

        // Check if user exists in Firestore
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (!userDoc.exists()) {
            // Create a new profile for first-time Google sign-ins
            const userData = {
                uid: user.uid,
                email: user.email,
                fullName: user.displayName || 'Anonymous User',
                phone: user.phoneNumber || '',
                userType: 'Individual',
                createdAt: new Date(),
                photoURL: user.photoURL || ''
            };
            await setDoc(doc(db, "users", user.uid), userData);

            // Merge into local user state
            setUser({ ...user, ...userData });
        }

        return user;
    };

    const logout = () => {
        return signOut(auth);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // Fetch extra user details if needed, or just set the basic auth user
                // For now, let's try to get profile data to have the name available
                try {
                    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                    if (userDoc.exists()) {
                        setUser({ ...currentUser, ...userDoc.data() });
                    } else {
                        setUser(currentUser);
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                    setUser(currentUser);
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);


    const updateUserProfile = async (fullName, phone, extraData = {}) => {
        if (!user) return;

        const updateData = {
            fullName,
            phone,
            ...extraData
        };

        await setDoc(doc(db, "users", user.uid), updateData, { merge: true });

        // Update local state immediately
        setUser(prevUser => ({
            ...prevUser,
            fullName,
            phone,
            ...extraData
        }));
    };

    const value = {
        user,
        signup,
        login,
        loginWithGoogle,
        logout,
        updateUserProfile,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
